import * as models from "../../lib/api/models";
import styles from "./AccountSearchPage.module.css";
import classNames from "classnames";
import DateInput from "../../modules/common/inputs/DateInput";
import StyledButton from "../../modules/common/buttons/StyledButton";
import StyledInput from "../../modules/common/inputs/StyledInput";
import StyledZipcodeInput from "../../modules/common/inputs/StyledZipcodeInput";
import ValidationMessage from "../../modules/common/ValidationMessage/ValidationMessage";
import { formatToNumericsOnly } from "../../lib/formatting";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import {
  accountSearchBillingZipCodeSchema,
  accountSearchBirthDateSchema,
  accountSearchCardLast4Schema,
  accountSearchSSNLast4Schema,
  accountSearchThreeofFourRequestSchema,
} from "../../lib/validation/schemas";
import { useMemo, useState } from "react";
import { z, ZodError } from "zod";
import { getDisplayError, NetworkDisplayError } from "../../lib/api/endpoints";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

const strings = {
  searchByThree: "Search by 3 of 4 Fields",
  search: "Search",
  clear: "Clear",
  last4CardNumber: "Last 4 Card #",
  last4SSN: "Last 4 SSN",
  dateOfBirth: "Date of Birth",
  billingZip: "Billing Zip",
};

const ZIPCODE_NO_EXTENSION = 5;
const MAX_DATE_RANGE = "9999-12-31";
const SEARCH_BY_THREE_MIN_NUM_FIELDS = 3;

type Props = {
  isDisabled: boolean;
  isLoading: boolean;

  resetAccountSearchQueries: () => void;
  accountSearchByThreeOfFour: UseMutateAsyncFunction<
    models.AccountSearchResponse,
    unknown,
    models.AccountSearchByThreeOfFourRequest,
    unknown
  >;
  accountSearchByThreeOfFourResponse: models.AccountSearchResponse | undefined;
  setDisplayError: React.Dispatch<
    React.SetStateAction<NetworkDisplayError | undefined>
  >;
  setAccountToView: React.Dispatch<
    React.SetStateAction<models.AccountSearchResult | undefined>
  >;
};

export default function ThreeOfFourSearchForm({
  isDisabled,
  isLoading,
  resetAccountSearchQueries,
  accountSearchByThreeOfFour,
  accountSearchByThreeOfFourResponse,
  setDisplayError,
  setAccountToView,
}: Props) {
  const [cardLast4, setCardLast4] = useState("");
  const [ssnLast4, setSSNLast4] = useState("");
  const [birthDate, setBirthDate] = useState<models.DateString>("");
  const [billingZipCode, setBillingZipCode] = useState("");
  const [validationMessages, setValidationMessages] =
    useState<models.ValidationErrors>();

  const accountSearchThreeOfFourHasInvalidInputs = useMemo(() => {
    try {
      accountSearchThreeofFourRequestSchema.parse({
        cardLast4,
        ssnLast4,
        birthDate,
        billingZipCode,
      });
    } catch (err) {
      if (err instanceof ZodError && err.errors.length > 0) {
        return true;
      }
    }
    return false;
  }, [billingZipCode, birthDate, cardLast4, ssnLast4]);

  const canSubmitSearchByThree =
    (cardLast4 ? 1 : 0) +
      (ssnLast4 ? 1 : 0) +
      (birthDate ? 1 : 0) +
      (billingZipCode ? 1 : 0) >=
      SEARCH_BY_THREE_MIN_NUM_FIELDS &&
    !accountSearchThreeOfFourHasInvalidInputs;

  const validateSearchByThreeOfFourAgainstSchema = (
    fieldToParseKey: string,
    fieldToParseValue: string,
    schema: z.ZodEffects<z.ZodString, string, string>,
  ) => {
    try {
      schema.parse(fieldToParseValue);
    } catch (err) {
      if (err instanceof ZodError && err.errors.length > 0) {
        setValidationMessages((prev) => ({
          ...prev,
          [fieldToParseKey]: [err.errors[0].message],
        }));
        return;
      }
    }
  };

  const onDisplayGeneralErrors = (err: unknown) => {
    setDisplayError(getDisplayError(err));
  };

  const onSearchAccountByThreeOfFour = (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmitSearchByThree) {
      return;
    }

    resetAccountSearchQueries();
    setValidationMessages(undefined);

    accountSearchByThreeOfFour({
      cardLast4,
      ssnLast4,
      birthDate,
      billingZipCode,
    })
      .then((res) =>
        res.accounts.length > 0 && res.accounts.length === 1
          ? setAccountToView(res.accounts[0])
          : undefined,
      )
      .catch((err) =>
        setValidationErrorsAndDisplayGeneralErrors(
          err,
          setValidationMessages,
          onDisplayGeneralErrors,
        ),
      );
  };

  const onClearSearchByThreeOfFour = () => {
    setCardLast4("");
    setSSNLast4("");
    setBirthDate("");
    setBillingZipCode("");
    setValidationMessages(undefined);
  };

  return (
    <form
      className={styles.searchContainer}
      onSubmit={onSearchAccountByThreeOfFour}
    >
      <h2 className={classNames(styles.heading, styles.searchHeading)}>
        {strings.searchByThree}
      </h2>
      <div className={styles.searchByThreeInnerContainer}>
        <div className={styles.searchByThreeInputContainer}>
          <StyledInput
            className={styles.searchByThreeInput}
            label={strings.last4CardNumber}
            value={cardLast4}
            onChange={(e) => {
              setCardLast4(formatToNumericsOnly(e.currentTarget.value));
              setValidationMessages((prev) => ({
                ...prev,
                ["cardLast4"]: [],
              }));
            }}
            onBlur={() =>
              validateSearchByThreeOfFourAgainstSchema(
                "cardLast4",
                cardLast4,
                accountSearchCardLast4Schema,
              )
            }
            maxLength={4}
            invalid={
              !!validationMessages?.cardLast4 &&
              validationMessages.cardLast4.length > 0
            }
          />
          <StyledInput
            className={styles.searchByThreeInput}
            label={strings.last4SSN}
            value={ssnLast4}
            onChange={(e) => {
              setSSNLast4(formatToNumericsOnly(e.currentTarget.value));
              setValidationMessages((prev) => ({
                ...prev,
                ["ssnLast4"]: [],
              }));
            }}
            onBlur={() =>
              validateSearchByThreeOfFourAgainstSchema(
                "ssnLast4",
                ssnLast4,
                accountSearchSSNLast4Schema,
              )
            }
            maxLength={4}
            invalid={
              !!validationMessages?.ssnLast4 &&
              validationMessages.ssnLast4.length > 0
            }
          />
          <DateInput
            className={styles.dateOfBirth}
            inputClassName={classNames({
              [styles.invalidDateOfBirthLabel]:
                !!validationMessages?.birthDate &&
                validationMessages.birthDate.length > 0,
            })}
            label={strings.dateOfBirth}
            value={birthDate}
            onChange={(e) => {
              setBirthDate(e.currentTarget.value as models.DateString);
              setValidationMessages((prev) => ({
                ...prev,
                ["birthDate"]: [],
              }));
            }}
            onBlur={() =>
              validateSearchByThreeOfFourAgainstSchema(
                "birthDate",
                birthDate,
                accountSearchBirthDateSchema,
              )
            }
            max={MAX_DATE_RANGE}
          />
          <StyledZipcodeInput
            className={styles.searchByThreeInput}
            label={strings.billingZip}
            value={billingZipCode}
            onChange={(v) => {
              setBillingZipCode(v);
              setValidationMessages((prev) => ({
                ...prev,
                ["billingZipCode"]: [],
              }));
            }}
            onBlur={() =>
              validateSearchByThreeOfFourAgainstSchema(
                "billingZipCode",
                billingZipCode,
                accountSearchBillingZipCodeSchema,
              )
            }
            maxLength={ZIPCODE_NO_EXTENSION}
            invalid={
              !!validationMessages?.billingZipCode &&
              validationMessages.billingZipCode.length > 0
            }
          />
        </div>
        <div className={styles.searchByThreeButtonContainer}>
          <StyledButton
            variant="transparent"
            onClick={onClearSearchByThreeOfFour}
          >
            {strings.clear}
          </StyledButton>
          <StyledButton
            className={styles.searchByThreeSubmit}
            type="submit"
            disabled={!canSubmitSearchByThree || isDisabled}
            isLoading={isLoading}
          >
            {strings.search}
          </StyledButton>
        </div>
      </div>
      {validationMessages ? (
        <div className={styles.validationMessages}>
          {validationMessages.cardLast4 ? (
            <ValidationMessage
              validationMessages={validationMessages.cardLast4}
            />
          ) : null}
          {validationMessages.ssnLast4 ? (
            <ValidationMessage
              validationMessages={validationMessages.ssnLast4}
            />
          ) : null}
          {validationMessages.birthDate ? (
            <ValidationMessage
              validationMessages={validationMessages.birthDate}
            />
          ) : null}
          {validationMessages.billingZipCode ? (
            <ValidationMessage
              validationMessages={validationMessages.billingZipCode}
            />
          ) : null}
        </div>
      ) : accountSearchByThreeOfFourResponse?.message ? (
        <ValidationMessage
          validationMessages={[accountSearchByThreeOfFourResponse.message]}
        />
      ) : null}
    </form>
  );
}
